import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Box, Button, Menu, useMantineTheme } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAnyFormDirty } from '@/store/slices/form/transmissionReportFormSlice';
import { setActiveColorCategory } from '@/store/slices/transmissionReports/transmissionReportsSlice';
import logger from '@/utils/Logger';
import { useAuth } from '@/hooks/useAuth';
import { useBoxInclinicFeatureFlag } from '@/hooks/useBoxInclinicFeatureFlag';
import { useDashboardFeature } from '@/hooks/useNewDashboardFeature';
import { usePatientListFeatureFlag } from '@/hooks/usePatientListFeature';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useTransmissionReportFeature } from '@/hooks/useTransmissionReportFeature';
import routes from '@/routes/config';
import { AnalyticsIcon, LogoIcon } from '@/assets/icons';
import DashboardWhiteIcon from '@/assets/icons/DashboardWhiteIcon';
import { FeatureFlagSettingsIcon } from '@/assets/icons/FeatureFlagSettingsIcon';
import InClinicIcon from '@/assets/icons/InClinicIcon';
import LogoutPlumIcon from '@/assets/icons/LogoutPlumIcon';
import PatientsWhiteIcon from '@/assets/icons/PatientsWhiteIcon';
import SettingsIcon from '@/assets/icons/SettingsIcon';
import SwitchPortalPlumIcon from '@/assets/icons/SwitchPortalPlumIcon';
import TransmissionsIcon from '@/assets/icons/TransmissionsIcon';

import CreatePatientModal from '@/pages/physician/CreatePatient/CreatePatient';
import { ReportColor } from '@/types/transmissionReport';
import { UserRole } from '@/types/user';
import ConfirmationModal from '@/ui/ConfirmationModal';

import classes from './TopNav.module.scss';

const opsUrl = import.meta.env.VITE_OPS_APP_URL ?? '#';

interface NavItem {
  link: string;
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  matchPaths?: string[];
  safeNavigate?: boolean;
}

export function TopNav() {
  const theme = useMantineTheme();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { logout } = useAuth();
  const { getDefaultPatientListRoute } = usePatientListFeatureFlag();
  const { isBoxInclinicFeatureEnabled } = useBoxInclinicFeatureFlag();
  const { getDefaultReportRoute: getTransmissionReportRoute } = useTransmissionReportFeature();
  const { getDashboardRoute } = useDashboardFeature();
  const { user } = useAppSelector((state) => state.auth);
  const isPatientCreateEnabled = user?.organizations
    ? user?.organizations[0]?.parent?.isCreatePatientInRs
    : false;
  const userRole = user?.role as UserRole;
  const isUserAdmin =
    userRole === UserRole.InternalSuperAdmin || userRole === UserRole.InternalAdmin;
  const isUserSuperAdmin = userRole === UserRole.InternalSuperAdmin;
  const isAnyFormDirty = useAppSelector(selectIsAnyFormDirty);
  const { requestNavigation, confirmNavigation, cancelNavigation, isModalOpen } =
    useSafeNavigate(isAnyFormDirty);
  const isOnTransmissionReportPage = location.pathname.includes(
    routes.physician.defaultTransmissionReport
  );

  const [isCreatePatientOpen, setIsCreatePatientOpen] = useState(false);

  const openCreateReportPatient = () => setIsCreatePatientOpen(true);
  const closeCreateReportPatient = () => setIsCreatePatientOpen(false);

  const handleLogout = async () => {
    try {
      // Allow logout (Okta redirect) to proceed without triggering
      // the review beforeunload confirmation dialog.
      sessionStorage.setItem('skipReviewBeforeUnload', '1');
      await logout();
    } catch (error) {
      logger.error('Logout failed:', error as Error);
    }
  };

  const navItems: NavItem[] = [
    {
      link: getDashboardRoute(),
      key: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardWhiteIcon />,
      matchPaths: [routes.physician.dashboard, routes.physician.dashboard]
    },
    {
      link: getDefaultPatientListRoute(),
      key: 'patients',
      label: 'Patients',
      icon: <PatientsWhiteIcon />,
      matchPaths: ['/physician/patients', '/physician/patients/list']
    },
    {
      link: getTransmissionReportRoute(),
      key: 'transmissions',
      label: 'Transmissions',
      icon: <TransmissionsIcon />,
      matchPaths: [routes.physician.defaultTransmissionReport, routes.physician.reviewTransmissions]
    },
    {
      link: routes.physician.analytics.accountOverview,
      key: 'analytics',
      label: 'Analytics',
      icon: <AnalyticsIcon />
    },
    ...(isBoxInclinicFeatureEnabled
      ? [
          {
            link: routes.physician.inClinic,
            key: 'in-clinic',
            label: 'In-Clinics',
            icon: <InClinicIcon />
          }
        ]
      : [])
  ];

  const rightDropDownItems: NavItem[] = [
    ...(isUserSuperAdmin
      ? [
          {
            key: 'feature-flags',
            label: 'Feature Flags',
            icon: <FeatureFlagSettingsIcon />,
            onClick: () => {
              window.location.href = routes.user.settings;
            },
            link: routes.user.settings
          }
        ]
      : []),
    ...(isUserAdmin
      ? [
          {
            key: 'operations-portal',
            label: 'Operations Portal',
            icon: <SwitchPortalPlumIcon />,
            onClick: () => {
              window.location.href = opsUrl;
            },
            link: opsUrl
          }
        ]
      : []),
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutPlumIcon />,
      onClick: handleLogout,
      link: '#'
    }
  ];

  const renderNavLink = (item: NavItem) => {
    const isActive = item.matchPaths
      ? item.matchPaths.some((path) => location.pathname.startsWith(path))
      : location.pathname === item.link;

    const handleClick = (e: React.MouseEvent) => {
      if (item.key === 'transmissions') {
        dispatch(setActiveColorCategory({ color: ReportColor.Red }));
      }
      if (item.onClick) {
        e.preventDefault();
        item.onClick();
      } else if (isOnTransmissionReportPage) {
        e.preventDefault();
        requestNavigation(item.link);
      }
    };

    return (
      <Link
        to={item.link}
        className={classes.link}
        data-active={isActive || undefined}
        key={item.key}
        onClick={handleClick}
        style={
          {
            '--theme-color-plum-5': theme.colors.plum[5]
          } as React.CSSProperties
        }>
        <Box className={classes.icon}>{item.icon}</Box>
        <Box component="span" className={classes.label}>
          {item.label}
        </Box>
      </Link>
    );
  };

  const LogoHeader = () => (
    <Button className={classes.logoButton} variant="transparent">
      <Box className={classes.header}>
        <LogoIcon />
      </Box>
    </Button>
  );

  const renderMenuItem = (item: (typeof rightDropDownItems)[number]) => (
    <Menu.Item key={item.key} onClick={item.onClick} className={classes.navMenuItem}>
      <Box className={classes.navMenuItemBox}>
        {item.icon}
        {item.label}
      </Box>
    </Menu.Item>
  );

  return (
    <>
      <nav className={classes.navbar}>
        <div className={classes.logoSection}>
          <LogoHeader />
        </div>

        <div className={classes.navbarMain}>{navItems.map(renderNavLink)}</div>

        {isPatientCreateEnabled && (
          <Button
            onClick={openCreateReportPatient}
            style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
            <IconPlus style={{ marginRight: '4px' }} />
            Create Patient
          </Button>
        )}

        <div className={classes.rightSection}>
          <Menu shadow="md" position="bottom-end" width={250}>
            <Menu.Target>
              <Button variant="transparent" size="xs" px={0}>
                <Box>
                  <SettingsIcon />
                </Box>
              </Button>
            </Menu.Target>

            <Menu.Dropdown className={classes.navDropdown}>
              {rightDropDownItems.map(renderMenuItem)}
            </Menu.Dropdown>
          </Menu>
        </div>
      </nav>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={cancelNavigation}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to leave this page?"
        onConfirm={confirmNavigation}
        isLoading={false}
      />

      {isCreatePatientOpen && (
        <CreatePatientModal isOpen={isCreatePatientOpen} onClose={closeCreateReportPatient} />
      )}
    </>
  );
}
